import React, { useState } from 'react';
import { Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { roomCategories } from '../mock';

export const Accommodation = () => {
  const [selectedRoom, setSelectedRoom] = useState(null);

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section
        className="relative h-96 flex items-center justify-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=1920&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0" style={{ backgroundColor: 'rgba(16, 40, 84, 0.7)' }} />
        <div className="relative z-10 text-center px-4">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
            Accommodation
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">
            Elegantly designed rooms and suites crafted for your comfort and relaxation
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: '#102854' }}>
            Your Perfect Home Away From Home
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            At Pauline Hotels, every room is a sanctuary of comfort and style. Whether you're traveling for business or leisure, 
            our thoughtfully designed accommodations provide the perfect blend of luxury, functionality, and warmth. 
            Each space features premium amenities, elegant furnishings, and meticulous attention to detail.
          </p>
        </div>
      </section>

      {/* Room Categories */}
      <section className="py-16 px-4" style={{ backgroundColor: '#f9fafb' }}>
        <div className="container mx-auto">
          {roomCategories.map((room, index) => (
            <div
              key={room.id}
              className={`mb-20 last:mb-0 ${
                index % 2 === 0 ? '' : ''
              }`}
            >
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                <div className="grid grid-cols-1 lg:grid-cols-2">
                  {/* Images */}
                  <div className={`relative h-96 lg:h-auto ${
                    index % 2 === 0 ? 'lg:order-1' : 'lg:order-2'
                  }`}>
                    <div className="grid grid-cols-2 h-full">
                      {room.images.map((image, imgIndex) => (
                        <div
                          key={imgIndex}
                          className="relative overflow-hidden cursor-pointer group"
                          onClick={() => setSelectedRoom(room.id)}
                        >
                          <img
                            src={image}
                            alt={`${room.name} ${imgIndex + 1}`}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                          />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Details */}
                  <div className={`p-8 lg:p-12 flex flex-col justify-center ${
                    index % 2 === 0 ? 'lg:order-2' : 'lg:order-1'
                  }`}>
                    <div className="inline-block px-4 py-1 rounded-full text-sm font-semibold mb-4" style={{ backgroundColor: '#FEF3E2', color: '#C9A961' }}>
                      {room.size} | {room.guests}
                    </div>
                    <h3 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: '#102854' }}>
                      {room.name}
                    </h3>
                    <p className="text-gray-600 text-lg mb-6 leading-relaxed">
                      {room.description}
                    </p>

                    {/* Amenities */}
                    <div className="mb-8">
                      <h4 className="text-lg font-semibold mb-3" style={{ color: '#102854' }}>
                        Amenities & Features
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {room.amenities.map((amenity, idx) => (
                          <div key={idx} className="flex items-start space-x-2">
                            <Check size={18} className="mt-0.5 flex-shrink-0" style={{ color: '#C9A961' }} />
                            <span className="text-gray-600 text-sm">{amenity}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <Button
                      asChild
                      size="lg"
                      className="font-semibold transition-all hover:scale-105"
                      style={{
                        backgroundColor: '#C9A961',
                        color: '#ffffff'
                      }}
                    >
                      <a href="/#booking">Book {room.name}</a>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: '#102854' }}>
            Need Help Choosing?
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Our team is here to help you find the perfect accommodation for your needs
          </p>
          <Button
            asChild
            size="lg"
            variant="outline"
            className="font-semibold transition-all hover:scale-105"
            style={{
              borderColor: '#C9A961',
              color: '#102854'
            }}
          >
            <a href="/contact">Contact Our Team</a>
          </Button>
        </div>
      </section>
    </div>
  );
};